'use client';

import { useMemo } from 'react';
import { skipToken, useQuery } from '@tanstack/react-query';
import type { Render } from '@/types/render';
import { isUserVisibleRenderType } from '@/types/render';
import { RenderStatus } from '@/shared/constants';
import { queryClient } from '@/shared/lib/query-client';
import { cacheKeys } from '@/shared/lib/cache-invalidation';

/**
 * Server-state cache for render entities. Owned by TanStack Query
 * (`cacheKeys.renders.list(conceptId)`). The WS handler, mutation onSuccess,
 * and initial concept-detail hydration all funnel through `upsertRender` /
 * `setRendersForConcept` here. See STATE_POLICY.md.
 */

function readListFromCache(conceptId: string | undefined): Render[] {
  if (!conceptId) return EMPTY;
  return (
    queryClient.getQueryData<Render[]>(cacheKeys.renders.list(conceptId)) ??
    EMPTY
  );
}

const EMPTY: Render[] = [];

export function setRendersForConcept(
  conceptId: string,
  renders: Render[]
): void {
  queryClient.setQueryData<Render[]>(
    cacheKeys.renders.list(conceptId),
    renders
  );
  renders.forEach((render) => {
    queryClient.setQueryData<Render>(
      cacheKeys.renders.detail(render.id),
      render
    );
  });
}

function deriveConceptId(render: Render): string | undefined {
  return render.relationships?.concept_angle?.meta?.concept_id;
}

export function upsertRender(render: Render, conceptIdHint?: string): void {
  const conceptId = conceptIdHint ?? deriveConceptId(render);
  queryClient.setQueryData<Render>(
    cacheKeys.renders.detail(render.id),
    render
  );
  if (!conceptId) return;
  queryClient.setQueryData<Render[]>(
    cacheKeys.renders.list(conceptId),
    (prev) => {
      const list = prev ?? [];
      const idx = list.findIndex((r) => r.id === render.id);
      if (idx === -1) return [...list, render];
      const next = list.slice();
      next[idx] = render;
      return next;
    }
  );
}

/**
 * A render as a WS push describes it: its id plus only the fields that event
 * actually carries.
 */
export interface RenderPatch {
  id: string;
  attributes?: Partial<Render['attributes']>;
  relationships?: Render['relationships'];
}

/**
 * Merge a PARTIAL render into the cache — the shape a realtime push carries.
 *
 * A WS payload is an optimistic delta, not a resource: `renders/completed`
 * names the status, the file and the lineage, and says nothing about
 * `settings`, `created_at` or the parent-render meta the REST resource already
 * put in the cache. Writing it through `upsertRender` replaced the whole
 * object, so a finished render silently LOST its settings — the render form
 * then read a settings-less render and reset the Explore tab to a blank form
 * (no Brands, no colours, no styles, no prompt) until the page was reloaded.
 * Every field the event does not mention must therefore survive it; the API
 * stays the source of truth and fills the rest on the next refresh.
 */
export function patchRender(patch: RenderPatch, conceptIdHint?: string): void {
  const existing =
    queryClient.getQueryData<Render>(cacheKeys.renders.detail(patch.id)) ??
    readListFromCache(conceptIdHint).find((r) => r.id === patch.id);

  upsertRender(mergeRender(existing, patch), conceptIdHint);
}

/**
 * Overlay a patch on a cached render. An absent key means "unchanged", so
 * `undefined` values are dropped rather than written — a payload that omits a
 * field must never blank it. Relationships merge one level down so a push that
 * only knows the new file URL keeps the parent-render meta beside it.
 */
function mergeRender(existing: Render | undefined, patch: RenderPatch): Render {
  if (!existing) {
    return {
      type: 'render',
      id: patch.id,
      attributes: dropUndefined(patch.attributes ?? {}),
      relationships: patch.relationships,
    } as Render;
  }

  const relationships = { ...existing.relationships };
  for (const [key, value] of Object.entries(patch.relationships ?? {})) {
    if (value === undefined) continue;
    const previous = (relationships as Record<string, unknown>)[key];
    (relationships as Record<string, unknown>)[key] =
      previous && typeof previous === 'object'
        ? { ...(previous as object), ...(value as object) }
        : value;
  }

  return {
    ...existing,
    id: patch.id,
    attributes: {
      ...existing.attributes,
      ...dropUndefined(patch.attributes ?? {}),
    },
    relationships,
  };
}

function dropUndefined<T extends object>(source: T): T {
  return Object.fromEntries(
    Object.entries(source).filter(([, value]) => value !== undefined)
  ) as T;
}

export function removeRenderFromCache(
  renderId: string,
  conceptIdHint?: string
): void {
  queryClient.removeQueries({
    queryKey: cacheKeys.renders.detail(renderId),
  });
  if (conceptIdHint) {
    queryClient.setQueryData<Render[]>(
      cacheKeys.renders.list(conceptIdHint),
      (prev) => (prev ? prev.filter((r) => r.id !== renderId) : prev)
    );
    return;
  }
  // No conceptId hint — scan all per-concept lists.
  const cache = queryClient.getQueryCache();
  cache
    .findAll({ queryKey: ['renders', 'list'] })
    .forEach((query) => {
      const data = query.state.data as Render[] | undefined;
      if (!data || !data.some((r) => r.id === renderId)) return;
      queryClient.setQueryData<Render[]>(
        query.queryKey,
        data.filter((r) => r.id !== renderId)
      );
    });
}

export function getRendersForConcept(
  conceptId: string | undefined
): Render[] {
  return readListFromCache(conceptId);
}

export function useRendersForConcept(
  conceptId: string | undefined
): Render[] {
  const { data } = useQuery<Render[]>({
    queryKey: cacheKeys.renders.list(conceptId ?? '__none__'),
    queryFn: skipToken,
    enabled: false,
    initialData: () => readListFromCache(conceptId),
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return data ?? EMPTY;
}

function sortByCreatedAtAsc(renders: Render[]): Render[] {
  return [...renders].sort(
    (a, b) =>
      new Date(a.attributes.created_at).getTime() -
      new Date(b.attributes.created_at).getTime()
  );
}

export function filterRendersByAngle(
  renders: Render[],
  angleId: string | undefined
): Render[] {
  if (!angleId) return EMPTY;
  return sortByCreatedAtAsc(
    renders.filter(
      (r) => r.relationships?.concept_angle?.data?.id === angleId
    )
  );
}

export function filterCompletedRendersByAngle(
  renders: Render[],
  angleId: string | undefined
): Render[] {
  if (!angleId) return EMPTY;
  return sortByCreatedAtAsc(
    renders.filter(
      (r) =>
        r.relationships?.concept_angle?.data?.id === angleId &&
        r.attributes.status === RenderStatus.COMPLETED &&
        isUserVisibleRenderType(r.attributes.render_type)
    )
  );
}

export function filterCompletedRendersWithFilesByAngle(
  renders: Render[],
  angleId: string | undefined
): Render[] {
  if (!angleId) return EMPTY;
  return sortByCreatedAtAsc(
    renders.filter((r) => {
      if (r.relationships?.concept_angle?.data?.id !== angleId) return false;
      if (r.attributes.status !== RenderStatus.COMPLETED) return false;
      if (!isUserVisibleRenderType(r.attributes.render_type)) return false;
      return Boolean(
        r.relationships?.file?.meta?.url || r.attributes.file_url
      );
    })
  );
}

export function useRendersByAngle(
  conceptId: string | undefined,
  angleId: string | undefined
): Render[] {
  const renders = useRendersForConcept(conceptId);
  return useMemo(
    () => filterRendersByAngle(renders, angleId),
    [renders, angleId]
  );
}

export function useCompletedRendersWithFilesByAngle(
  conceptId: string | undefined,
  angleId: string | undefined
): Render[] {
  const renders = useRendersForConcept(conceptId);
  return useMemo(
    () => filterCompletedRendersWithFilesByAngle(renders, angleId),
    [renders, angleId]
  );
}
